import type { HTMLAttributes, ReactNode, Ref } from 'react';
import clsx from 'clsx';
import './StatTile.css';

export type StatTileTone = 'primary' | 'xp' | 'streak' | 'info' | 'accent' | 'neutral';

export interface StatTileProps extends Omit<HTMLAttributes<HTMLElement>, 'onClick'> {
  /** Icon element (SVG via <Icon>), rendered in the tinted corner tile. */
  icon: ReactNode;
  /** The big number / headline value. */
  value: ReactNode;
  /** What the number is (xs, bold, muted). */
  label: string;
  /** Optional second line under the label (records, hints). */
  sub?: string;
  tone?: StatTileTone;
  /** When set, the tile renders as a real button with press physics. */
  onPress?: () => void;
  ref?: Ref<HTMLElement>;
}

/**
 * StatTile — dashboard stat card (streak, XP, words, lessons, awards, and
 * the future coins balance). Bold-pass construction: surface card with a
 * crisp border and hard extruded edge; the value carries the tone color,
 * chrome stays neutral. Pass `onPress` to make the whole tile tappable
 * (44px+ target, Button-style press-down physics).
 */
export function StatTile({
  icon,
  value,
  label,
  sub,
  tone = 'neutral',
  onPress,
  className,
  ref,
  ...rest
}: StatTileProps) {
  const content = (
    <>
      <span className="fluent-stat-tile__icon" aria-hidden="true">
        {icon}
      </span>
      <span className="fluent-stat-tile__value">{value}</span>
      <span className="fluent-stat-tile__label">{label}</span>
      {sub && <span className="fluent-stat-tile__sub">{sub}</span>}
    </>
  );

  if (onPress) {
    return (
      <button
        type="button"
        className={clsx('fluent-stat-tile', className)}
        data-tone={tone}
        data-pressable=""
        onClick={onPress}
        ref={ref as Ref<HTMLButtonElement>}
        {...rest}
      >
        {content}
      </button>
    );
  }
  return (
    <div
      className={clsx('fluent-stat-tile', className)}
      data-tone={tone}
      ref={ref as Ref<HTMLDivElement>}
      {...rest}
    >
      {content}
    </div>
  );
}
